import { motion } from "framer-motion";
const PILLARS = [
  {
    key: "TRU",
    title: "TRUST",
    desc: "At Truviq, trust is the foundation of everything we do. We strive to build strong, transparent relationships with our clients, employees, and partners.",
    badge: "01 // INTEGRITY",
    accent: "from-blue-500/10 to-indigo-500/5",
    borderGlow: "group-hover:border-blue-500/40",
  },
  {
    key: "I",
    title: "INNOVATION",
    desc: "Innovation drives us forward. At Truviq, we believe in fostering creativity and pushing boundaries to unlock new possibilities for our clients and the industries we serve",
    badge: "03 // MOMENTUM",
    accent: "from-purple-500/10 to-pink-500/5",
    borderGlow: "group-hover:border-purple-500/40",
  },
  {
    key: "V",
    title: "VALUE",
    desc: "Delivering exceptional value is at the heart of our mission. We focus on creating solutions that not only meet but exceed the needs of our clients.",
    badge: "02 // RETURN",
    accent: "from-emerald-500/10 to-teal-500/5",
    borderGlow: "group-hover:border-emerald-500/40",
  },

  {
    key: "Q",
    title: "QUALITY",
    desc: "Quality is non-negotiable at Truviq. From the smallest detail to the most complex project, we uphold the highest standards of excellence. ",
    badge: "04 // PRECISION",
    accent: "from-amber-500/10 to-orange-500/5",
    borderGlow: "group-hover:border-amber-500/40",
  },
];

export default function CorePillarsCircuit() {
  return (
    <section className="relative w-full py-28 md:py-36 bg-[#fffffe] text-slate-900 overflow-hidden select-none">
      <div className="mx-auto xl:w-[95%] 3xl:w-4/5 px-6 xl:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 mb-4">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
              Operating Foundation
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-slate-950">
            The Four Pillars That Power{" "}
            <span className="font-serif italic font-light text-[#62d4f7]">
              Our Purpose
            </span>
          </h2>
        </div>
        {/* Orthogonal Circuit Schematic Layout */}
        <div className="relative min-h-[620px] flex flex-col justify-between">
          {/* SVG Animated Connector Matrix (Desktop Only) */}
          <svg
            className="hidden lg:block absolute inset-0 w-full h-full pointer-events-none z-0"
            viewBox="0 0 1200 600"
            fill="none"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#000036" stopOpacity="0.25" />
                <stop offset="50%" stopColor="#000036" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#000036" stopOpacity="0.25" />
              </linearGradient>
            </defs>
            {/* Top-Left: TRUST to TRU */}
            <motion.path
              d="M 420 100 L 480 100 L 480 260"
              stroke="url(#lineGrad)"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
            <circle cx="420" cy="100" r="3.5" fill="#000036" />
            <circle cx="480" cy="260" r="3" fill="#000036" />
            {/* Top-Right: VALUE to V */}
            <motion.path
              d="M 780 100 L 680 100 L 680 260"
              stroke="url(#lineGrad)"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
            />
            <circle cx="780" cy="100" r="3.5" fill="#000036" />
            <circle cx="680" cy="260" r="3" fill="#000036" />
            {/* Bottom-Left: INNOVATION to I */}
            <motion.path
              d="M 420 510 L 610 510 L 610 340"
              stroke="url(#lineGrad)"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            />
            <circle cx="420" cy="510" r="3.5" fill="#000036" />
            <circle cx="610" cy="340" r="3" fill="#000036" />
            {/* Bottom-Right: QUALITY to Q */}
            <motion.path
              d="M 780 510 L 750 510 L 750 340"
              stroke="url(#lineGrad)"
              strokeWidth="1.5"
              strokeDasharray="4 4"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
            />
            <circle cx="780" cy="510" r="3.5" fill="#000036" />
            <circle cx="750" cy="340" r="3" fill="#000036" />
          </svg>
          {/* Central Monolith Hub: TRU-V-I-Q Core */}
          <div className="hidden lg:flex absolute inset-0 m-auto  items-center justify-center z-20 pointer-events-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="relative px-8 py-5 flex flex-col items-center justify-center"
            >
              <span className="font-display text-7xl font-black tracking-[0.10em] text-[#000036]">
                TRU<span className="text-slate-400">-</span>V
                <span className="text-slate-400">-</span>I
                <span className="text-slate-400">-</span>Q
              </span>
            </motion.div>
          </div>
          {/* TOP ROW: Pillar 1 & Pillar 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start relative z-10">
            {/* Top-Left: TRUST (Col 1 to 4) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-4 group"
            >
              <div
                className={`p-8 rounded-xl overflow-hidden relative border border-slate-300/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.03)] transition-all duration-300 ${PILLARS[0].borderGlow} hover:-translate-y-1`}
              >
                <div
                  className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
                  style={{
                    background:
                      "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
                  }}
                />
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-slate-400">
                      [{PILLARS[0].key}]
                    </span>
                    <h3 className="font-display text-xl font-bold tracking-wider text-[#000036] uppercase">
                      {PILLARS[0].title}
                    </h3>
                  </div>
                </div>
                <p className="text-base text-[#000036] font-normal leading-relaxed">
                  {PILLARS[0].desc}
                </p>
              </div>
            </motion.div>
            {/* Spacer Column in Top Row (Col 5 to 8) */}
            <div className="hidden lg:block lg:col-span-4" />
            {/* Top-Right: VALUE (Col 9 to 12) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-4 group"
            >
              <div
                className={`p-8 rounded-xl relative overflow-hidden bg-white/90 border border-slate-300/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.03)] transition-all duration-300 ${PILLARS[1].borderGlow} hover:-translate-y-1`}
              >
                <div
                  className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
                  style={{
                    background:
                      "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
                  }}
                />
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-slate-400">
                      [{PILLARS[1].key}]
                    </span>
                    <h3 className="font-display text-xl font-bold tracking-wider text-[#000036] uppercase">
                      {PILLARS[1].title}
                    </h3>
                  </div>
                </div>
                <p className="text-base text-[#000036] font-normal leading-relaxed">
                  {PILLARS[1].desc}
                </p>
              </div>
            </motion.div>
          </div>
          {/* Mobile Nexus Node Fallback */}
          <div className="flex lg:hidden justify-center my-6">
            <div className="px-6 py-3 rounded-xl bg-white border border-slate-300 shadow-sm text-center">
              <span className="font-display text-xl font-bold tracking-widest text-[#000036]">
                TRU-V-I-Q
              </span>
            </div>
          </div>
          {/* BOTTOM ROW: Pillar 3 & Pillar 4 */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end relative z-10 lg:mt-24">
            {/* Bottom-Left: INNOVATION (Col 1 to 4) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="lg:col-span-4 group"
            >
              <div
                className={`p-8 rounded-xl relative overflow-hidden bg-white/90 border border-slate-300/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.03)] transition-all duration-300 ${PILLARS[2].borderGlow} hover:-translate-y-1`}
              >
                <div
                  className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
                  style={{
                    background:
                      "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
                  }}
                />
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-slate-400">
                      [{PILLARS[2].key}]
                    </span>
                    <h3 className="font-display text-xl font-bold tracking-wider text-[#000036] uppercase">
                      {PILLARS[2].title}
                    </h3>
                  </div>
                </div>
                <p className="text-base text-[#000036] font-normal leading-relaxed">
                  {PILLARS[2].desc}
                </p>
              </div>
            </motion.div>
            <div className="hidden lg:block lg:col-span-4" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="lg:col-span-4 group"
            >
              <div
                className={`p-8 relative rounded-xl overflow-hidden bg-white/90 border border-slate-300/80 backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.03)] transition-all duration-300 ${PILLARS[3].borderGlow} hover:-translate-y-1`}
              >
                <div
                  className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
                  style={{
                    background:
                      "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
                  }}
                />
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-slate-400">
                      [{PILLARS[3].key}]
                    </span>
                    <h3 className="font-display text-xl font-bold tracking-wider text-[#000036] uppercase">
                      {PILLARS[3].title}
                    </h3>
                  </div>
                </div>
                <p className="text-base text-[#000036] font-normal leading-relaxed">
                  {PILLARS[3].desc}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
